import React, { useCallback, useEffect, useState } from 'react';
import { DashboardLayout } from '../components/layout';
import { useAdminNavigation } from '../hooks/useAdminNavigation';
import * as adminApi from '../../../api/adminApi';

/**
 * Alert Management Matrix (QA Phase-2: "central Alert Management Matrix is not
 * visible within the admin layout"). Read-only view of every user alert across
 * the platform — criteria, status, and trigger activity.
 */
const AdminAlertsPage: React.FC = () => {
  const {
    sidebarCollapsed, setSidebarCollapsed, activeSection,
    setSelectedRegulation, handleNavClick, handleLogout,
  } = useAdminNavigation();

  const [rows, setRows] = useState<adminApi.AdminAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const token = window.localStorage.getItem('policyora_token');

  const load = useCallback(() => {
    if (!token) return;
    setLoading(true);
    setError(null);
    adminApi.getAdminAlerts(token)
      .then((d) => setRows(d.alerts))
      .catch((err) => setError(err.message || 'Failed to load alerts'))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const activeCount = rows.filter((r) => r.is_active).length;
  const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString() : '—');
  const chips = (arr: string[]) => (arr && arr.length ? arr.join(', ') : '—');

  const th: React.CSSProperties = { textAlign: 'left', padding: '0.6rem 0.8rem', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--ds-text-muted)', borderBottom: '1px solid var(--ds-border)', whiteSpace: 'nowrap' };
  const td: React.CSSProperties = { padding: '0.6rem 0.8rem', fontSize: '0.84rem', color: 'var(--ds-text)', borderBottom: '1px solid var(--ds-surface-2)', verticalAlign: 'top' };

  return (
    <DashboardLayout
      collapsed={sidebarCollapsed}
      activeSection={activeSection}
      welcomeLabel="Alert Management"
      selectedRegulation={null}
      onToggleSidebar={() => setSidebarCollapsed((prev) => !prev)}
      onNavClick={handleNavClick}
      onProfileClick={() => handleNavClick('profile')}
      onLogout={handleLogout}
      onRegulationModalClose={() => setSelectedRegulation(null)}
    >
      <section style={{ padding: '1.25rem 1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 800, color: 'var(--ds-text)' }}>Alert Management Matrix</h2>
            <p style={{ margin: '4px 0 0', color: 'var(--ds-text-muted)', fontSize: '0.88rem' }}>
              All user alerts across the platform — criteria, status, and trigger activity.
            </p>
          </div>
          <button type="button" onClick={load} style={{ padding: '0.45rem 0.9rem', borderRadius: 8, border: '1px solid var(--ds-border-strong)', background: 'transparent', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, color: 'var(--ds-text-secondary)' }}>↺ Refresh</button>
        </div>

        {!loading && !error && (
          <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
            <div style={{ background: 'var(--ds-surface-2)', border: '1px solid var(--ds-border)', borderRadius: 10, padding: '0.7rem 1rem' }}>
              <div style={{ fontSize: '0.72rem', color: 'var(--ds-text-muted)', textTransform: 'uppercase' }}>Total alerts</div>
              <div style={{ fontSize: '1.3rem', fontWeight: 800 }}>{rows.length}</div>
            </div>
            <div style={{ background: 'var(--ds-surface-2)', border: '1px solid var(--ds-border)', borderRadius: 10, padding: '0.7rem 1rem' }}>
              <div style={{ fontSize: '0.72rem', color: 'var(--ds-text-muted)', textTransform: 'uppercase' }}>Active</div>
              <div style={{ fontSize: '1.3rem', fontWeight: 800, color: 'var(--ds-success-text)' }}>{activeCount}</div>
            </div>
          </div>
        )}

        {loading ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--ds-text-muted)' }}>Loading alerts…</div>
        ) : error ? (
          <div style={{ padding: '1rem', borderRadius: 8, background: 'rgba(240,68,56,0.08)', color: 'var(--ds-danger-text)' }}>
            {error} <button type="button" onClick={load} style={{ marginLeft: 8, cursor: 'pointer' }}>Retry</button>
          </div>
        ) : rows.length === 0 ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--ds-text-muted)' }}>No alerts configured yet.</div>
        ) : (
          <div style={{ overflowX: 'auto', border: '1px solid var(--ds-border)', borderRadius: 12 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 920 }}>
              <thead>
                <tr>
                  <th style={th}>User</th>
                  <th style={th}>Alert</th>
                  <th style={th}>Status</th>
                  <th style={th}>Jurisdictions</th>
                  <th style={th}>Keywords</th>
                  <th style={th}>Freq</th>
                  <th style={th}>Triggers</th>
                  <th style={th}>Last fired</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((a) => (
                  <tr key={a.id}>
                    <td style={td}>{a.user_email || '—'}</td>
                    <td style={td}><strong>{a.name}</strong>{a.semantic_query ? <div style={{ color: 'var(--ds-text-muted)', fontSize: '0.75rem' }}>“{a.semantic_query}”</div> : null}</td>
                    <td style={td}>
                      <span style={{ fontSize: '0.72rem', fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: a.is_active ? 'rgba(3,152,85,0.1)' : 'var(--ds-surface-2)', color: a.is_active ? 'var(--ds-success)' : 'var(--ds-text-muted)' }}>
                        {a.is_active ? 'Active' : 'Paused'}
                      </span>
                    </td>
                    <td style={td}>{chips(a.jurisdictions)}</td>
                    <td style={td}>{chips(a.keywords)}</td>
                    <td style={td}>{a.frequency}</td>
                    <td style={td}>{a.trigger_count}</td>
                    <td style={td}>{fmt(a.last_triggered_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </DashboardLayout>
  );
};

export default AdminAlertsPage;
